import { useEffect, useState } from 'react';
import { app, describeError, go, resetWizard } from '../state/app';
import { getCommission, getFamilyMe } from '../api/endpoints';
import { setBalance } from '../lib/devlog';
import { mediaUrl } from '../lib/media';
import { poll } from '../lib/poll';

/**
 * The wait.
 *
 * This screen used to show the pipeline: "credit hold → shot plan → cache
 * check → generate the gaps → moderate", a status string, a shot count and the
 * raw tag JSON. That is a debug view. A parent does not need to know there is
 * a planner, a cache or a model involved, and telling them makes a magic
 * moment feel like a build log.
 *
 * So it shows one thing instead: a still of the opening scene, which
 * pipeline/preview.py generates in a few seconds while the video takes about a
 * minute. Something real to look at, arriving roughly ten times sooner than
 * the thing it previews.
 */
export function CommissionProgress() {
  const { commissionId, commission, characterName } = app.use();
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!commissionId) {
      go('gallery');
      return;
    }
    getFamilyMe()
      .then((me) => setBalance(me.creditBalance, me.ledger ?? []))
      .catch(() => undefined);

    const handle = poll({
      fetch: () => getCommission(commissionId),
      onValue: (value) => app.set({ commission: value }),
      done: (value) => value.status === 'awaiting_approval' || value.status === 'failed',
      onError: (err) => setError(describeError(err)),
      onTimeout: () => setTimedOut(true),
      intervalMs: 2000,
      timeoutMs: 10 * 60 * 1000,
    });
    return () => handle.cancel();
  }, [commissionId]);

  const status = commission?.status ?? 'starting';
  const done = status === 'awaiting_approval';
  const failed = status === 'failed';
  const preview = mediaUrl(commission?.previewKey);
  const name = characterName || 'Your character';

  return (
    <>
      <div className="pane-img">
        {preview ? (
          // The first real glimpse. Held slightly dimmed until the film is
          // ready, so "still working" reads without a word of explanation.
          <img
            className={`preview-still${done ? '' : ' preview-working'}`}
            src={preview}
            alt="A scene from the film being made"
          />
        ) : (
          <div className={`placeholder ${done || failed ? '' : 'pulse'}`}>
            {failed ? '' : '✨'}
          </div>
        )}
      </div>

      <div className="pane-q">
        {/* THE BACK BUTTON THIS SCREEN DID NOT HAVE, and its absence is what
            sent Jamie to the browser's own. Every other wizard screen carries
            one; this one did not, so the only way "back" from the screen that
            has just spent credits was the iPad's left-edge swipe — which, with
            nothing in the app's history, reloaded the page onto "Who does your
            kid love?" with the film he had just paid for nowhere on it.
            state/app.ts's BACK table sends the gesture here too; this is the
            same destination made visible. NOT the summary: that screen has
            "Make it — 4 credits" on it and the wizard is over. */}
        <button className="backbtn" onClick={() => go('library')}>
          ‹ library
        </button>
        {failed ? (
          <>
            <h1>Something went wrong</h1>
            <p className="sub">
              We couldn’t finish this one, so we’ve put your credits back.
              Nothing was charged.
            </p>
          </>
        ) : done ? (
          <>
            <h1>{name}’s film is ready</h1>
            <p className="sub">
              Have a watch first. Nothing reaches your little one until you say
              so.
            </p>
          </>
        ) : (
          <>
            <h1>Making {name}’s film</h1>
            <p className="sub">
              This usually takes a minute or two. You can leave this page — it
              will be waiting in your library.
            </p>
          </>
        )}

        {/* Deliberately no status string, no shot count, no tag JSON and no
            pipeline diagram. If something is genuinely wrong the parent gets a
            plain sentence, not an internal error. */}
        {error && !failed && (
          <div className="err">
            We’re having trouble checking on this. Your film is still being
            made — try the library in a moment.
          </div>
        )}
        {timedOut && !done && !failed && (
          <div className="err">
            This one is taking longer than usual. It will appear in your library
            when it’s done.
          </div>
        )}

        <button className="big" onClick={() => go('library')}>
          {done ? 'Watch it →' : 'Go to the library'}
        </button>
        <button
          className="ghost"
          style={{ marginTop: 10, alignSelf: 'flex-start' }}
          onClick={() => {
            resetWizard();
            go('gallery');
          }}
        >
          Make another character
        </button>
      </div>
    </>
  );
}
